import type { CSSProperties, ReactNode } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../../atoms/Card';
import { cn } from '../../lib/utils';

export interface WidgetCardShellProps {
  title: string;
  /** Optional icon rendered before the title. */
  icon?: ReactNode;
  /** Content for the right side of the header — typically filter dropdowns. */
  headerSlot?: ReactNode;
  /** Widget body. */
  children: ReactNode;
  /** 1-indexed CSS grid column start. */
  gridColumn?: number;
  /** CSS grid column span. Defaults to 1. */
  gridColumnSpan?: number;
  /** 1-indexed CSS grid row start. */
  gridRow?: number;
  /** CSS grid row span. Defaults to 1. */
  gridRowSpan?: number;
  minHeight?: number | string;
  className?: string;
  /** Override the default body padding. */
  contentClassName?: string;
}

export function WidgetCardShell({
  title,
  icon,
  headerSlot,
  children,
  gridColumn,
  gridColumnSpan = 1,
  gridRow,
  gridRowSpan = 1,
  minHeight,
  className,
  contentClassName,
}: WidgetCardShellProps) {
  const style: CSSProperties = {};
  if (gridColumn !== undefined) style.gridColumn = `${gridColumn} / span ${gridColumnSpan}`;
  if (gridRow !== undefined) style.gridRow = `${gridRow} / span ${gridRowSpan}`;
  if (minHeight !== undefined) style.minHeight = minHeight;

  return (
    // `data-widget-card` is the boundary `usePopoverPlacement` keeps popovers inside.
    // min-w-0: a grid item otherwise refuses to shrink below its content's min-content width.
    <Card
      data-widget-card=""
      className={cn('min-w-0 rounded-[4px] border-0 bg-white shadow-none', className)}
      style={style}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-5 pb-0">
        <div className="flex items-center gap-1">
          {icon}
          <CardTitle className="text-[14px] font-medium capitalize leading-[1.2] tracking-normal text-dawn-black">
            {title}
          </CardTitle>
        </div>
        {headerSlot}
      </CardHeader>

      <CardContent className={cn('p-5 pt-6', contentClassName)}>{children}</CardContent>
    </Card>
  );
}

WidgetCardShell.displayName = 'WidgetCardShell';
